import dayjs from "dayjs";
import { GeofenceDirection } from "../services/model/AttendanceException";

/**
 * Walking-speed benchmark — ~5 km/h. Used to convert geofence-violation
 * distance into an estimated walking time the resolver can apply to the
 * original clock event.
 */
export const GEOFENCE_WALKING_SPEED_M_PER_MIN = 80;

/**
 * Distance cap above which no estimate is offered. Anyone clocking from
 * further than this is almost certainly not walking — they're in a
 * vehicle, on the wrong site, or the GPS is malfunctioning. The modal
 * surfaces a manual-review alert instead of a pre-filled time.
 */
export const GEOFENCE_DISTANCE_CAP_METERS = 5000;

export const isGeofenceBeyondCap = (distanceMeters: number): boolean =>
  distanceMeters > GEOFENCE_DISTANCE_CAP_METERS;

// Matches a strict ISO-8601 datetime prefix (e.g. "2026-05-21T03:27:47.479186Z").
// Restrictive on purpose: the 12-hour parser accepts "6:08 am" with optional
// whitespace via \s*, so a permissive datetime heuristic would short-circuit
// that path and lose valid user input.
const ISO_DATETIME_PREFIX = /^\d{4}-\d{2}-\d{2}T/;

export const parseClockTime = (s: string): number | null => {
  // ISO 8601 strings come from the API as the exception timestamp
  // (e.g. "2026-05-21T03:27:47.479186Z"). Parse via dayjs so the
  // minutes-of-day are computed in the viewer's local timezone.
  if (ISO_DATETIME_PREFIX.test(s)) {
    const d = dayjs(s);
    return d.isValid() ? d.hour() * 60 + d.minute() : null;
  }
  // Native <input type="time"> emits a 24-hour "HH:MM" (optionally ":SS").
  // Accept it directly so the time picker never has to go through the
  // am/pm parser (which is the source of the "Invalid input" errors).
  const h24 = s.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (h24) {
    const h = parseInt(h24[1], 10);
    const mins = parseInt(h24[2], 10);
    if (Number.isNaN(h) || Number.isNaN(mins)) return null;
    if (h < 0 || h > 23 || mins < 0 || mins > 59) return null;
    return h * 60 + mins;
  }
  const m = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mins = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(mins)) return null;
  if (h < 1 || h > 12 || mins < 0 || mins > 59) return null;
  const ap = m[3].toLowerCase();
  if (ap === "pm" && h !== 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  return h * 60 + mins;
};

/**
 * Formats an exception timestamp (ISO or already-12h) for display, in the
 * viewer's local timezone.
 */
export const formatGeofenceTimestamp = (raw: string): string => {
  if (!raw) return "";
  if (ISO_DATETIME_PREFIX.test(raw)) {
    const d = dayjs(raw);
    return d.isValid() ? d.format("h:mma") : raw;
  }
  return raw;
};

/** Format minutes-of-day as a 24-hour "HH:MM" — the value a native
 *  <input type="time"> expects. */
export const formatClockTime24 = (totalMinutes: number): string => {
  const wrapped = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

export const formatGeofenceDistance = (m: number): string => {
  if (m < 1000) return `${m} m`;
  const km = m / 1000;
  return `${km % 1 === 0 ? km.toFixed(0) : km.toFixed(1)} km`;
};

export const computeGeofenceAdjustedTime = (
  baseTimestamp: string,
  direction: GeofenceDirection,
  distanceMeters: number
): { adjusted: string; deltaMinutes: number } | null => {
  if (isGeofenceBeyondCap(distanceMeters)) return null;
  const deltaMinutes = Math.ceil(
    distanceMeters / GEOFENCE_WALKING_SPEED_M_PER_MIN
  );
  const baseMinutes = parseClockTime(baseTimestamp);
  if (baseMinutes === null) return null;
  const adjustedMinutes =
    direction === "in"
      ? baseMinutes + deltaMinutes
      : baseMinutes - deltaMinutes;
  return { adjusted: formatClockTime24(adjustedMinutes), deltaMinutes };
};

/**
 * Great-circle distance in metres between two lat/lng points (haversine).
 * Mirrors the backend geofence check (`_haversine_meters` in the monolith) so
 * the dashboard colours clock pins consistently with how violations are
 * detected server-side.
 */
export const haversineMeters = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const EARTH_RADIUS_M = 6371000;
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
};

/** True when (lat,lng) falls within `radiusM` metres of the geofence centre. */
export const isWithinGeofence = (
  lat: number,
  lng: number,
  centerLat: number,
  centerLng: number,
  radiusM: number
): boolean => haversineMeters(lat, lng, centerLat, centerLng) <= radiusM;
